import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ShoppingBag } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const navLinks = [
    { href: '#about', label: 'À propos' },
    { href: '#pricing', label: 'Tarifs' },
    { href: '#solutions', label: 'Solutions digitales' },
    { href: '#testimonials', label: 'Témoignages' },
    { href: '#contact', label: 'Contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (href === '/boutique') {
      window.location.href = '/boutique';
      return;
    }
    if (!isHomePage) {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[150] transition-all duration-500 ${
        isScrolled
          ? 'bg-black/95 backdrop-blur-md border-b border-[#C9A55C]/20'
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 flex-shrink-0" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_service-devis-1/artifacts/32x86mnu_IMG_4011.jpeg" 
              alt="HELP ADN Logo" 
              className="h-14 w-auto"
            />
            <span className="text-2xl font-bold tracking-wider font-serif whitespace-nowrap">
              <span className="text-white">HELP</span>
              <span className="text-[#C9A55C]">ADN</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-6 ml-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-xs font-medium tracking-wider uppercase text-white/70 hover:text-[#C9A55C] transition-colors duration-300 whitespace-nowrap"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/guide-renovation"
              className="text-xs font-medium tracking-wider uppercase text-white/70 hover:text-[#C9A55C] transition-colors duration-300 whitespace-nowrap"
              data-testid="nav-guide-renovation"
            >
              Aides rénovation
            </Link>
            <Link
              to="/guide-aides"
              className="text-xs font-medium tracking-wider uppercase text-white/70 hover:text-[#C9A55C] transition-colors duration-300 whitespace-nowrap"
              data-testid="nav-guide-aides"
            >
              Aides officielles
            </Link>
            <Link
              to="/boutique"
              className="text-xs font-medium tracking-wider uppercase text-[#C9A55C] hover:text-[#C9A55C]/80 transition-colors duration-300 whitespace-nowrap"
              data-testid="nav-boutique"
            >
              Boutique
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden xl:flex items-center gap-4 flex-shrink-0">
            <Link 
              to="/boutique"
              className="flex items-center gap-2 bg-[#C9A55C] text-black px-4 py-2 text-sm font-bold tracking-wider uppercase hover:bg-[#C9A55C]/90 transition-colors"
              data-testid="header-boutique"
            >
              <ShoppingBag className="w-4 h-4" />
              Boutique
            </Link>
            <a 
              href="tel:0684632855" 
              className="flex items-center gap-2 text-sm text-[#C9A55C] whitespace-nowrap"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">06 84 63 28 55</span>
            </a>
            <a 
              href="https://calendly.com/vanderberguejennifer"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold rounded-none px-6 py-3 text-sm font-semibold tracking-wider uppercase inline-flex items-center"
              data-testid="header-rdv"
            >
              Prendre RDV
            </a>
            <Button 
              onClick={(e) => scrollToSection(e, '#contact')}
              className="border border-[#C9A55C]/50 bg-transparent text-[#C9A55C] hover:bg-[#C9A55C]/10 rounded-none px-6 py-3 text-sm font-semibold tracking-wider uppercase"
              data-testid="header-cta"
            >
              Devis gratuit
            </Button>
          </div>

          {/* Mobile Menu Button - Always shows hamburger */}
          <button
            className="xl:hidden p-4 text-[#C9A55C]"
            onClick={toggleMenu}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            <Menu className="w-8 h-8" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`xl:hidden fixed inset-0 bg-black z-[9998] transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        data-testid="mobile-menu"
      >
          {/* Header with logo and close button */}
          <div className="flex items-center justify-between h-20 px-4 border-b border-[#C9A55C]/30 bg-black">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_service-devis-1/artifacts/32x86mnu_IMG_4011.jpeg" 
                alt="HELP ADN Logo" 
                className="h-10 w-auto"
              />
              <span className="text-lg font-bold tracking-wider font-serif">
                <span className="text-white">HELP</span>
                <span className="text-[#C9A55C]">ADN</span>
              </span>
            </div>
            <button
              onClick={toggleMenu}
              className="p-4 text-[#C9A55C]"
              data-testid="mobile-menu-close"
              aria-label="Fermer le menu"
            >
              <X className="w-8 h-8" />
            </button>
          </div>
          
          <nav className="flex flex-col p-6 gap-0 bg-black">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-base font-medium tracking-wider uppercase text-white hover:text-[#C9A55C] transition-colors py-4 border-b border-[#C9A55C]/20"
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/guide-renovation"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-base font-medium tracking-wider uppercase text-white hover:text-[#C9A55C] transition-colors py-4 border-b border-[#C9A55C]/20"
            >
              Aides à la rénovation
            </Link>
            <Link
              to="/guide-aides"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-base font-medium tracking-wider uppercase text-white hover:text-[#C9A55C] transition-colors py-4 border-b border-[#C9A55C]/20"
            >
              Aides officielles
            </Link>
            <Link
              to="/boutique"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-base font-medium tracking-wider uppercase text-[#C9A55C] hover:text-[#C9A55C]/80 transition-colors py-4 border-b border-[#C9A55C]/20"
              data-testid="mobile-nav-boutique"
            >
              Boutique
            </Link>
            <div className="pt-6 space-y-4">
              <a href="tel:0684632855" className="flex items-center gap-2 text-[#C9A55C] text-sm">
                <Phone className="w-4 h-4" />
                <span>06 84 63 28 55</span>
              </a>
              <a 
                href="https://calendly.com/vanderberguejennifer"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-gold rounded-none py-3 text-sm font-semibold tracking-wider uppercase text-center block"
              >
                Prendre RDV
              </a>
              <Button 
                onClick={(e) => scrollToSection(e, '#contact')}
                className="w-full border border-[#C9A55C]/50 bg-transparent text-[#C9A55C] hover:bg-[#C9A55C]/10 rounded-none py-3 text-sm font-semibold tracking-wider uppercase"
              >
                Devis gratuit
              </Button>
            </div>
          </nav>
        </div>
    </header>
  );
};

export default Header;
